import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { colLayout, corralLayout, frostLayout, hexToWorld, type BattleState, type ColLayout, type CorralLayout, type FrostLayout, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { CaminoLayer, HOURS, type Hour } from './camino.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { SnowFall } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The cleric's last road, chapter 3 (docs/sendas/clerigo.md, «La nieve»): the south face in the blizzard, on chapter 1's
 * craft (engine/camino.ts: the night's light, the snow's ground, the lantern's pool, the chapel, the mirrors, the beam).
 * The snow comes in hard from the left; the lantern's pool cools from warm to blue as Tessa's cold drops.
 *
 * - `snowCol` (act 1), `snowStorm` (the cinematic), `colFoot` (the opening): the way up the col between drifts; small
 *   shrines by it with slate roofs, each with its brazier (an iron bowl on three legs: lit, its fire, embers rising and a
 *   warm pool on the snow; out, dark with a thread of smoke). Joran's band behind with its own warm light.
 * - `frostTerrace` (act 2, the puzzle): the snow chapel at the door; the lantern on a fixed post by the entrance; five
 *   mirrors under a crust of frost that melts when thawed and grows back, each with its number cut on a stone plate at
 *   its foot; six stone bases, each with its circle of two hexes cut faint in the snow (warm while its brazier burns);
 *   the pile of braziers by the stretcher, one in the hero's hands while it carries one; the two ways to the door faint
 *   in the frost (Aud's marks); the beam, and the door's lamp lit with a flash.
 * - `corralNight` (act 3, the test), `corralFire` (the campfire), `corralDawn` (the close): the dry-stone ring of Piet's
 *   parents' corral with its gaps, straw, sheep lying in it, the family's bed; three braziers; the ash watcher's shadow
 *   on the floor while it is coming (dark wings growing over a red ring); one of the family frozen glows ice-blue; near
 *   the end of the night the sky pales toward dawn.
 *
 * Draw calls on top of chapter 1's: the braziers that come and go (one instanced mesh), the bases' circles (one mesh),
 * the two faint ways (one mesh), the watcher's shadow (one), a frost crust a mirror. The fires and the embers are the
 * glow layer's; the board's one point light stays the stretcher's lantern (docs/RENDIMIENTO.md).
 */

export const NIEVE_PLACES = new Set(['snowCol', 'snowStorm', 'colFoot', 'frostTerrace', 'corralNight', 'corralFire', 'corralDawn']);

const S = WORLD_PER_HEX;
const WHITE = new THREE.Color('#f4f8ff');
const COLD = new THREE.Color('#b9ccff');
const ICE = new THREE.Color('#9fd0ff');
const RED = new THREE.Color('#ff4f3a');
const FIRE = new THREE.Color('#ff6a3a');
const FIRE_CORE = new THREE.Color('#ffd9c8');
const EMBER = new THREE.Color('#ff8a5a');
const STONE = ['#6c7079', '#61656e', '#767a83'];
const PALE = ['#8a8e96', '#80848c', '#959aa2'];
const SNOW = ['#c8d3e1', '#bccadb', '#d3dcea'];
/** Straw: grey-brown, never gold (it lies under the braziers' warm light). */
const STRAW = ['#6b6255', '#615a4f', '#736a5c'];
const WOOL = ['#c3c8cf', '#b6bcc4', '#cdd1d7'];
const IRON = '#2a2d32';

const allCells = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) out.push({ q, r });
  return out;
};
const hexLen = (q: number, r: number): number => Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));

/** The scenes' own boards (game/cine.ts): the blizzard's way, the col's foot, the corral by its fire and at dawn. */
export const nieveSceneMap = (place0: string): MapDef | null => {
  if (!NIEVE_PLACES.has(place0) || place0 === 'snowCol' || place0 === 'frostTerrace' || place0 === 'corralNight') return null;
  const R = 6;
  const rocks = allCells(R).filter((h) => {
    const p = hexToWorld(h.q, h.r);
    if (place0 === 'snowStorm') return Math.abs(p.x) > 1700;
    if (place0 === 'colFoot') return Math.abs(p.x) > 2800 || h.r <= -4;
    // The corral's wall, a ring of four, open to the south-west (the way in).
    return hexLen(h.q, h.r) === 4 && !(h.r >= 2 && p.x < 0);
  });
  return { radius: R + 2, bastion: R, sectors: [], kind: 'incursion', open: true, rocks };
};

/** A digit of seven bars (a, b, c, d, e, f, g): what is cut on a mirror's plate. */
const SEG: Record<number, string> = { 1: 'bc', 2: 'abged', 3: 'abgcd', 4: 'fgbc', 5: 'afgcd' };

interface Nv {
  col: ColLayout | null;
  frost: FrostLayout | null;
  corral: CorralLayout | null;
  /** Where each brazier stands (world), lit at the last draw (col and corral: kit; frost: the instanced ones). */
  braziers: THREE.Vector3[];
  lit: number[];
  /** The frost: the instanced braziers (six bases, three at the pile, one in the hands), the bases' circles. */
  inst: THREE.InstancedMesh | null;
  pileAt: THREE.Vector3[];
  carry: THREE.Vector3 | null;
  rings: THREE.Mesh | null;
  ringLit: Float32Array;
  ways: THREE.Mesh | null;
  waysMat: THREE.ShaderMaterial | null;
  shells: { m: THREE.Group; k: number }[];
  ice: number[];
  postTop: THREE.Vector3 | null;
  beamKey: string;
  /** The corral: the watcher's shadow, the family's places, the frozen. */
  shadow: THREE.Mesh | null;
  shadowMat: THREE.ShaderMaterial | null;
  frozen: THREE.Vector3[];
  fires: THREE.Vector3[];
  smokeT: number;
  dawn: number;
  /** The band behind on the col. */
  band: THREE.Vector3 | null;
  blizzard: number;
}

export class NieveLayer extends CaminoLayer {
  declare private nv: Nv;

  constructor(ctx: ConstructorParameters<typeof CaminoLayer>[0], map: MapDef, place0: string, s: BattleState) {
    super(ctx, map, place0, s);
    this.group.name = `nieve-${place0}`;
  }

  protected override buildOwn(kit: Kit, s: BattleState): boolean {
    this.nv = {
      col: null,
      frost: null,
      corral: null,
      braziers: [],
      lit: [],
      inst: null,
      pileAt: [],
      carry: null,
      rings: null,
      ringLit: new Float32Array(6),
      ways: null,
      waysMat: null,
      shells: [],
      ice: [],
      postTop: null,
      beamKey: '',
      shadow: null,
      shadowMat: null,
      frozen: [],
      fires: [],
      smokeT: 0,
      dawn: 0,
      band: null,
      blizzard: this.place === 'snowStorm' ? 1.9 : this.place === 'snowCol' || this.place === 'colFoot' ? 1.5 : this.place === 'frostTerrace' ? 0.8 : 1.0,
    };
    const p = this.place;
    if (p === 'snowCol') this.buildCol(kit, s);
    else if (p === 'snowStorm') this.buildStorm(kit);
    else if (p === 'colFoot') this.buildColFoot(kit);
    else if (p === 'frostTerrace') this.buildFrost(kit, s);
    else if (p === 'corralNight') this.buildCorral(kit, s);
    else this.buildCorralScene(kit);
    return true;
  }

  /* ---------- pieces ---------- */

  /** A brazier: an iron bowl on three legs, coals in it (its fire is the glow layer's). */
  private brazierInKit(kit: Kit, p: THREE.Vector3, k = 0): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const bowl = new THREE.CylinderGeometry(0.3, 0.17, 0.2, 10, 1, true);
    const coal = new THREE.CylinderGeometry(0.26, 0.26, 0.04, 10);
    for (let i = 0; i < 3; i++) {
      const a = k + (i / 3) * Math.PI * 2;
      kit.add(box, IRON, place(p.x + Math.cos(a) * 0.17, p.y + 0.2, p.z + Math.sin(a) * 0.17, -a, new THREE.Vector3(0.04, 0.42, 0.04), 0, (i - 1) * 0.15));
    }
    kit.add(bowl, IRON, place(p.x, p.y + 0.46, p.z));
    kit.add(coal, '#2b1d1a', place(p.x, p.y + 0.5, p.z));
    box.dispose();
    bowl.dispose();
    coal.dispose();
  }

  /** The brazier's geometry alone (at its foot), for the instanced ones. */
  private brazierGeo(): THREE.BufferGeometry {
    const kit = new Kit();
    this.brazierInKit(kit, new THREE.Vector3(0, 0, 0));
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.3, flatShading: true });
    const mesh = kit.build(mat, 'brazier') as THREE.Mesh;
    mat.dispose();
    return mesh.geometry;
  }

  /**
   * A shrine of the col, chapter 1's chapel made small: pale stone on a plinth, a niche with its small cold lamp, a
   * steep two-sided roof of slate under snow (the snow wide on it: seen from the board's camera the roof is what shows,
   * and bare slate at night read as a black box), a gable of pale stone in front and the Order's small iron cross.
   */
  private shrine(kit: Kit, p: THREE.Vector3, yaw: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const gable = new THREE.CylinderGeometry(0, 0.5, 1, 3, 1);
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    const sx0 = Math.cos(yaw);
    const sz0 = -Math.sin(yaw);
    // Plinth and body, pale stone; a darker course at the foot.
    kit.add(box, PALE[1] as string, place(p.x, p.y + 0.09, p.z, yaw, new THREE.Vector3(1.05, 0.18, 0.95)));
    kit.add(box, PALE[2] as string, place(p.x, p.y + 0.55, p.z, yaw, new THREE.Vector3(0.85, 0.8, 0.75)));
    for (const k of [-1, 1]) kit.add(box, PALE[0] as string, place(p.x + sx0 * k * 0.4, p.y + 0.5, p.z + sz0 * k * 0.4 + 0, yaw, new THREE.Vector3(0.12, 0.9, 0.8)));
    // The niche: a deep slate-blue recess (never black), its arch of pale stone, the lamp in it.
    kit.add(box, '#3a4456', place(p.x + fx * 0.36, p.y + 0.58, p.z + fz * 0.36, yaw, new THREE.Vector3(0.38, 0.46, 0.06)));
    kit.add(box, PALE[0] as string, place(p.x + fx * 0.39, p.y + 0.85, p.z + fz * 0.39, yaw, new THREE.Vector3(0.5, 0.09, 0.08)));
    kit.add(box, '#e8eef8', place(p.x + fx * 0.36, p.y + 0.55, p.z + fz * 0.36, yaw + Math.PI / 4, new THREE.Vector3(0.08, 0.11, 0.08)));
    // The roof: two steep slabs of slate, the snow lying almost all over them; the gable in front.
    for (const sx of [-1, 1]) {
      const ox = sx0 * sx * 0.25;
      const oz = sz0 * sx * 0.25;
      kit.add(box, '#596272', place(p.x + ox, p.y + 1.12, p.z + oz, yaw, new THREE.Vector3(0.62, 0.09, 1.05), 0, sx * -0.8));
      kit.add(box, SNOW[sx > 0 ? 0 : 2] as string, place(p.x + ox * 0.96, p.y + 1.17, p.z + oz * 0.96, yaw, new THREE.Vector3(0.56, 0.06, 1.0), 0, sx * -0.8));
    }
    kit.add(gable, PALE[2] as string, place(p.x + fx * 0.36, p.y + 1.1, p.z + fz * 0.36, yaw, new THREE.Vector3(0.86, 0.52, 0.06), Math.PI / 2, 0));
    // The cross of the Order on the ridge's front.
    kit.add(box, IRON, place(p.x + fx * 0.36, p.y + 1.55, p.z + fz * 0.36, yaw, new THREE.Vector3(0.035, 0.26, 0.035)));
    kit.add(box, IRON, place(p.x + fx * 0.36, p.y + 1.6, p.z + fz * 0.36, yaw, new THREE.Vector3(0.15, 0.035, 0.035)));
    box.dispose();
    gable.dispose();
    this.light(new THREE.Vector3(p.x + fx * 0.42, p.y + 0.58, p.z + fz * 0.42), WHITE, 0.9, 0.8, 0.5);
    this.light(new THREE.Vector3(p.x + fx * 0.5, p.y + 0.4, p.z + fz * 0.5), COLD, 1.8, 0.22, 0.3);
  }

  /** A wall of dry stone across a cell, its face along `yaw` (the corral's ring): three courses, the stones overlapping, snow on top. */
  private dryWall(kit: Kit, p: THREE.Vector3, yaw: number, k: number): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const tx = Math.cos(yaw);
    const tz = -Math.sin(yaw);
    for (let j = 0; j < 3; j++)
      for (let i = 0; i < 4; i++) {
        const o = (i - 1.5) * 0.34 + (j % 2) * 0.17 + (this.k(i, j, k * 9) - 0.5) * 0.06;
        const sz = 0.38 + this.k(i + 3, j, k * 7) * 0.12;
        kit.add(ico, PALE[(i + j + Math.floor(k * 3)) % 3] as string, place(p.x + tx * o, p.y + 0.15 + j * 0.24, p.z + tz * o, yaw + i + j, new THREE.Vector3(sz, 0.3, 0.42)));
      }
    kit.add(box, SNOW[Math.floor(k * 2.99)] as string, place(p.x, p.y + 0.74, p.z, yaw, new THREE.Vector3(1.25, 0.08, 0.42)));
    ico.dispose();
    box.dispose();
  }

  /** The col's drifts: mounds of snow over rock that run into each other (no wall, no rock). */
  private drifts(kit: Kit, rocks: { q: number; r: number }[]): void {
    // (Fine facets and flat mounds: big facets turned from the key light read as navy stones on the snow.)
    const blob = new THREE.IcosahedronGeometry(0.5, 3);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (const h of rocks) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 2);
      // (Tall enough to bury the board's own rock on the cell: lower, its dark faces showed through as navy stones.)
      const tall = 1.1 + k * 0.6;
      for (let i = 0; i < 4; i++) {
        const a = k * 6.28 + i * 2.1;
        const sz = 1.45 + this.k(h.q, h.r, 3 + i) * 0.5;
        // (The wind piles them from the left: long to the right.)
        kit.add(blob, SNOW[(i + Math.floor(k * 3)) % 3] as string, place(p.x + Math.cos(a) * 0.35, p.y + tall * 0.2, p.z + Math.sin(a) * 0.3, 0, new THREE.Vector3(sz * S * 1.15, tall * 0.6, sz * S * 0.9)));
      }
      // (No rock through them: `k` is a sine's, crowded at its ends, so «k > 0.93» was one cell in six, rows of navy stones.)
      void ico;
    }
    blob.dispose();
    ico.dispose();
  }

  /** A sheep lying in the straw: a wool body, its dark head, the ears. */
  private sheep(kit: Kit, p: THREE.Vector3, yaw: number, k: number): void {
    const blob = new THREE.IcosahedronGeometry(0.5, 1);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    kit.add(blob, WOOL[Math.floor(k * 2.99)] as string, place(p.x, p.y + 0.22, p.z, yaw, new THREE.Vector3(0.62, 0.42, 0.9)));
    kit.add(blob, WOOL[(Math.floor(k * 2.99) + 1) % 3] as string, place(p.x - fx * 0.1, p.y + 0.34, p.z - fz * 0.1, yaw, new THREE.Vector3(0.5, 0.3, 0.6)));
    kit.add(blob, '#2a2624', place(p.x + fx * 0.46, p.y + 0.3, p.z + fz * 0.46, yaw, new THREE.Vector3(0.22, 0.22, 0.3)));
    for (const sx of [-1, 1]) kit.add(box, '#2a2624', place(p.x + fx * 0.42 + Math.cos(yaw) * sx * 0.13, p.y + 0.38, p.z + fz * 0.42 - Math.sin(yaw) * sx * 0.13, yaw + sx * 0.6, new THREE.Vector3(0.12, 0.04, 0.06)));
    blob.dispose();
    box.dispose();
  }

  /** A patch of straw on the snow. */
  private straw(kit: Kit, p: THREE.Vector3, r: number, k: number): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (let i = 0; i < 5; i++) {
      const a = k * 6 + i * 1.3;
      const d = (i === 0 ? 0 : 0.45) * r;
      kit.add(ico, STRAW[(i + Math.floor(k * 3)) % 3] as string, place(p.x + Math.cos(a) * d, p.y + 0.04, p.z + Math.sin(a) * d, a, new THREE.Vector3(r * 1.1, 0.1, r * 0.9)));
    }
    ico.dispose();
  }

  /** A campfire: a ring of stones, crossed logs; its fire is the glow layer's. */
  private campfire(kit: Kit, p: THREE.Vector3): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      kit.add(ico, STONE[i % 3] as string, place(p.x + Math.cos(a) * 0.38, p.y + 0.06, p.z + Math.sin(a) * 0.38, a, 0.18));
    }
    for (let i = 0; i < 3; i++) kit.add(box, '#3a2c22', place(p.x, p.y + 0.1, p.z, (i / 3) * Math.PI, new THREE.Vector3(0.08, 0.08, 0.6)));
    ico.dispose();
    box.dispose();
    this.nv.fires.push(p.clone());
  }

  /* ---------- the col ---------- */

  private buildCol(kit: Kit, s: BattleState): void {
    const L = colLayout(s.seed);
    this.nv.col = L;
    this.drifts(kit, L.rocks);
    for (const c of L.chapels) {
      const b = this.ctx.at(c.x, c.y, 0);
      this.brazierInKit(kit, b, c.at);
      // The shrine behind its brazier, its front to the way.
      const sh = this.ctx.at(c.x + c.side * 900, c.y - 300, 0);
      this.shrine(kit, sh, c.side > 0 ? -Math.PI / 2 : Math.PI / 2);
      this.nv.braziers.push(b);
    }
  }

  private buildStorm(kit: Kit): void {
    this.drifts(kit, this.map.rocks ?? []);
    this.shrine(kit, this.hex(-2, -3), Math.PI / 2);
  }

  private buildColFoot(kit: Kit): void {
    this.drifts(kit, this.map.rocks ?? []);
    const b = this.hex(-2, -1);
    this.brazierInKit(kit, b);
    this.shrine(kit, this.hex(-3, -1).add(new THREE.Vector3(-0.2, 0, 0)), Math.PI / 2);
    this.nv.braziers.push(b);
    this.nv.lit.push(1);
  }

  /* ---------- the frozen mirrors ---------- */

  private buildFrost(kit: Kit, s: BattleState): void {
    const L = frostLayout(s.seed);
    this.nv.frost = L;
    this.chapelBody(kit, L.chapel, L.door.q, L.door.r);
    this.sconce(kit);
    // The lantern on its post by the entrance, its arm toward where the beam leaves.
    const d = hexToWorld([1, 1, 0, -1, -1, 0][L.lantern.dir] as number, [0, -1, -1, 0, 1, 1][L.lantern.dir] as number);
    const top = this.hookPost(kit, this.hex(L.lantern.q, L.lantern.r), Math.atan2(d.x, d.y));
    this.nv.postTop = top;
    const lamp = this.lanternGroup();
    lamp.position.copy(top);
    this.group.add(lamp);
    this.light(top, WHITE, 1.4, 0.8, 0.6);
    // Aud's inscription by the lantern.
    this.stele(kit, this.hex(L.lantern.q, L.lantern.r).add(new THREE.Vector3(0.75, 0, 0.35)));
    // The mirrors: their stands, a crust of frost on each disc, the number cut on a plate at the foot.
    const box = new THREE.BoxGeometry(1, 1, 1);
    const crustM = this.track(new THREE.MeshStandardMaterial({ color: '#e4eefa', roughness: 0.35, metalness: 0.0, emissive: '#7f9cc8', emissiveIntensity: 0.55, transparent: true, opacity: 0.92, flatShading: true }));
    const crustG = this.track(new THREE.IcosahedronGeometry(0.42, 1));
    const shardG = this.track(new THREE.ConeGeometry(0.06, 0.26, 4));
    this.mirrors = L.mirrors.map((m, i) => {
      const p = this.hex(m.q, m.r);
      const view = this.mirrorStand(kit, p);
      const crust = new THREE.Group();
      const shell = new THREE.Mesh(crustG, crustM);
      shell.scale.set(1, 1, 0.32);
      crust.add(shell);
      for (let j = 0; j < 5; j++) {
        const sh = new THREE.Mesh(shardG, crustM);
        const a = (j / 5) * Math.PI * 2 + i;
        sh.position.set(Math.cos(a) * 0.32, Math.sin(a) * 0.32, 0.02);
        sh.rotation.z = a - Math.PI / 2;
        crust.add(sh);
      }
      view.disc.add(crust);
      this.nv.shells.push({ m: crust, k: 1 });
      this.nv.ice.push(1);
      // The plate at the stand's foot, on the side toward the bottom of the board, the number cut dark in it.
      const px = p.x;
      const pz = p.z + 0.5;
      kit.add(box, PALE[2] as string, place(px, p.y + 0.22, pz, 0, new THREE.Vector3(0.36, 0.44, 0.08), -0.25));
      const seg = SEG[i + 1] ?? '';
      const w = 0.16;
      const hh = 0.13;
      const bars: Record<string, [number, number, boolean]> = { a: [0, hh, true], g: [0, 0, true], d: [0, -hh, true], f: [-w / 2, hh / 2, false], b: [w / 2, hh / 2, false], e: [-w / 2, -hh / 2, false], c: [w / 2, -hh / 2, false] };
      for (const ch of seg) {
        const [bx, by, hz] = bars[ch] as [number, number, boolean];
        kit.add(box, '#1a1c21', place(px + bx, p.y + 0.24 + by * 0.97, pz + 0.045 - by * 0.25, 0, hz ? new THREE.Vector3(w, 0.03, 0.02) : new THREE.Vector3(0.03, hh, 0.02), -0.25));
      }
      return view;
    });
    box.dispose();
    // The bases: stone slabs; their circles cut in the snow (one mesh, warm while the brazier on it burns).
    const slab = new THREE.CylinderGeometry(0.42, 0.48, 0.12, 6);
    const ringGs: THREE.BufferGeometry[] = [];
    L.bases.forEach((b, i) => {
      const p = this.hex(b.q, b.r);
      kit.add(slab, PALE[i % 3] as string, place(p.x, p.y + 0.06, p.z, Math.PI / 6));
      kit.add(slab, SNOW[0] as string, place(p.x, p.y + 0.125, p.z, Math.PI / 6, new THREE.Vector3(0.8, 0.1, 0.8)));
      const g = new THREE.RingGeometry(2.1 * S, 2.24 * S, 64, 1);
      g.rotateX(-Math.PI / 2);
      g.translate(p.x, p.y + 0.06, p.z);
      g.setAttribute('aB', new THREE.Float32BufferAttribute(new Float32Array(g.getAttribute('position').count).fill(i), 1));
      ringGs.push(g);
      // A small inner ring at the slab too.
      const g2 = new THREE.RingGeometry(0.55, 0.62, 24, 1);
      g2.rotateX(-Math.PI / 2);
      g2.translate(p.x, p.y + 0.07, p.z);
      g2.setAttribute('aB', new THREE.Float32BufferAttribute(new Float32Array(g2.getAttribute('position').count).fill(i), 1));
      ringGs.push(g2);
      this.nv.braziers.push(p);
    });
    slab.dispose();
    const rg = this.track(mergeGeometries(ringGs) as THREE.BufferGeometry);
    for (const g of ringGs) g.dispose();
    const ringMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uLit: { value: this.nv.ringLit }, uT: { value: 0 } },
        vertexShader: /* glsl */ `attribute float aB; uniform float uLit[6]; varying float vLit; varying vec3 vP;
          void main() { int i = int(aB + 0.5); vLit = uLit[i]; vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; varying float vLit; varying vec3 vP;
          void main() {
            // Cut in the snow: a faint cold line, warm and brighter while its brazier burns.
            float br = 0.75 + 0.25 * sin(atan(vP.z, vP.x) * 40.0);
            vec3 c = mix(vec3(0.62, 0.72, 0.95), vec3(1.0, 0.5, 0.32), vLit);
            float a = (0.2 + vLit * (0.42 + 0.1 * sin(uT * 3.0))) * br;
            gl_FragColor = vec4(c, a);
          }`,
        transparent: true,
        depthWrite: false,
      }),
    );
    const rings = new THREE.Mesh(rg, ringMat);
    rings.renderOrder = 3;
    rings.frustumCulled = false;
    this.group.add(rings);
    this.nv.rings = rings;
    // The braziers that come and go: six on the bases, three at the pile, one in the hands.
    const inst = new THREE.InstancedMesh(this.track(this.brazierGeo()), this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.3, flatShading: true })), 10);
    inst.castShadow = true;
    inst.frustumCulled = false;
    this.group.add(inst);
    this.nv.inst = inst;
    const pile = this.ctx.at(L.pile.x, L.pile.y, 0);
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + 0.4;
      this.nv.pileAt.push(pile.clone().add(new THREE.Vector3(Math.cos(a) * 0.36, 0, Math.sin(a) * 0.36)));
    }
    this.straw(kit, pile, 0.7, 0.3);
    // The two ways to the door, faint in the frost: Aud's marks of when it was used.
    this.buildWays(L);
    this.buildBeam();
    // (No shrubs: chapter 1's dark ones read as black specks on this wide terrace.)
  }

  /** The faint ways: dashed strips along the two ways' corners. */
  private buildWays(L: FrostLayout): void {
    const pos: number[] = [];
    const u: number[] = [];
    const v: number[] = [];
    const idx: number[] = [];
    const half = 0.34;
    for (const way of [L.short, L.long]) {
      let run = 0;
      for (let i = 0; i + 1 < way.length; i++) {
        const a0 = way[i] as { q: number; r: number };
        const b0 = way[i + 1] as { q: number; r: number };
        const a = this.hex(a0.q, a0.r, 0.07);
        const b = this.hex(b0.q, b0.r, 0.07);
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = (-dz / len) * half;
        const nz = (dx / len) * half;
        const k = pos.length / 3;
        pos.push(a.x + nx, a.y, a.z + nz, a.x - nx, a.y, a.z - nz, b.x + nx, b.y, b.z + nz, b.x - nx, b.y, b.z - nz);
        u.push(run, run, run + len, run + len);
        v.push(1, -1, 1, -1);
        idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
        run += len;
      }
    }
    const g = this.track(new THREE.BufferGeometry());
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aU', new THREE.Float32BufferAttribute(u, 1));
    g.setAttribute('aV', new THREE.Float32BufferAttribute(v, 1));
    g.setIndex(idx);
    const mat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uA: { value: 1 } },
        vertexShader: /* glsl */ `attribute float aU; attribute float aV; varying float vU; varying float vV;
          void main() { vU = aU; vV = aV; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uA; varying float vU; varying float vV;
          void main() {
            // Dashes of old frost-light, a soft edge: seen, never mistaken for the beam.
            float dash = step(0.45, fract(vU * 1.4));
            float edge = 1.0 - abs(vV);
            float a = dash * smoothstep(0.0, 0.5, edge) * 0.42 * uA;
            gl_FragColor = vec4(vec3(0.72, 0.86, 1.0) * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    const m = new THREE.Mesh(g, mat);
    m.renderOrder = 3;
    m.frustumCulled = false;
    this.group.add(m);
    this.nv.ways = m;
    this.nv.waysMat = mat;
  }

  /* ---------- the corral ---------- */

  private corralBody(kit: Kit, wall: { q: number; r: number }[], gaps: { q: number; r: number }[]): void {
    for (const h of wall) {
      const p = this.hex(h.q, h.r);
      // Along the ring: square to the way out from the middle.
      const yaw = Math.atan2(p.x, p.z);
      this.dryWall(kit, p, yaw, this.k(h.q, h.r, 3));
    }
    // The gaps' posts, a broken hurdle by one.
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const h of gaps) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 7);
      if (k < 0.5) continue;
      kit.add(box, '#3a2f27', place(p.x + 0.3, p.y + 0.5, p.z, 0, new THREE.Vector3(0.1, 1.0, 0.1)));
      kit.add(box, SNOW[0] as string, place(p.x + 0.3, p.y + 1.02, p.z, 0, new THREE.Vector3(0.12, 0.05, 0.12)));
    }
    box.dispose();
  }

  private buildCorral(kit: Kit, s: BattleState): void {
    const L = corralLayout(s.seed);
    this.nv.corral = L;
    this.corralBody(kit, L.wall, L.gaps);
    L.sheep.forEach((p, i) => {
      const w = this.ctx.at(p.x, p.y, 0);
      this.straw(kit, w, 0.6, i * 0.37);
      this.sheep(kit, w, i * 1.7, (i * 0.31) % 1);
    });
    // The family's bed: straw and a wide blanket of grey wool under them.
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (const f of L.family) {
      const w = this.ctx.at(f.x, f.y, 0);
      this.straw(kit, w, 0.8, f.x * 0.001);
      kit.add(box, '#5d636c', place(w.x, w.y + 0.06, w.z, 0.3, new THREE.Vector3(0.9, 0.04, 1.1)));
    }
    box.dispose();
    for (const b of L.braziers) {
      const w = this.ctx.at(b.x, b.y, 0);
      this.brazierInKit(kit, w, b.x);
      this.nv.braziers.push(w);
    }
    // Hay bales by the wall, a feed trough.
    this.buildShadow();
  }

  private buildCorralScene(kit: Kit): void {
    const rocks = this.map.rocks ?? [];
    this.corralBody(kit, rocks, []);
    const sheepAt: [number, number][] = [
      [2, 1],
      [-1, 3],
      [3, -2],
      [-3, 1],
    ];
    sheepAt.forEach(([q, r], i) => {
      const w = this.hex(q, r);
      this.straw(kit, w, 0.6, i * 0.41);
      this.sheep(kit, w, i * 2.1, (i * 0.29) % 1);
    });
    this.straw(kit, this.hex(-1, 1), 0.9, 0.2);
    this.straw(kit, this.hex(1, 0), 0.8, 0.6);
    if (this.place === 'corralDawn') {
      // The apprentices' fire, off to the side, its last coals.
      this.campfire(kit, this.hex(-3, 4));
    }
  }

  /** The watcher's shadow: dark wings over a red ring on the floor, growing while it comes. */
  private buildShadow(): void {
    const mat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uK: { value: 0 }, uT: { value: 0 } },
        vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uK; uniform float uT; varying vec2 vUv;
          void main() {
            vec2 p = (vUv - 0.5) * 2.0;
            float r = length(p);
            // The wings: wide, swept, with feathered notches; the body down the middle.
            float ax = abs(p.x);
            float wing = step(ax, 0.95) * smoothstep(0.02, 0.0, abs(p.y + 0.18 * ax * ax - 0.12) - 0.3 * (1.0 - ax) * (0.75 + 0.25 * sin(ax * 22.0)));
            float body = smoothstep(0.02, 0.0, length(p / vec2(0.16, 0.5)) - 1.0);
            float dark = max(wing, body) * (0.45 + 0.45 * uK);
            // The red ring where it lands, tightening as it comes.
            float rr = mix(1.0, 0.92, uK);
            float ring = smoothstep(0.035, 0.0, abs(r - rr)) * (0.5 + 0.5 * sin(uT * 10.0));
            vec3 c = mix(vec3(0.02, 0.02, 0.05), vec3(1.0, 0.31, 0.23), ring);
            float a = max(dark * 0.85, ring * 0.9);
            if (a < 0.01) discard;
            gl_FragColor = vec4(c, a);
          }`,
        transparent: true,
        depthWrite: false,
      }),
    );
    const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1)), mat);
    m.rotation.x = -Math.PI / 2;
    m.renderOrder = 3;
    m.visible = false;
    m.frustumCulled = false;
    this.group.add(m);
    this.nv.shadow = m;
    this.nv.shadowMat = mat;
  }

  /* ---------- the light, the storm, dawn ---------- */

  override apply(terrain: TerrainBuild, hour: Hour = 'night'): void {
    super.apply(terrain, this.place === 'corralDawn' ? 'dawn' : hour);
    // The blizzard: the snow in hard from the left (chapter 1's falls slowly).
    if (this.fall) {
      this.fall.points.removeFromParent();
      this.fall.dispose();
    }
    const b = this.nv.blizzard;
    // (On a phone, 320: the col measured 195–293 ms/s with the machine loaded; the flakes are one draw call, but each is a
    // transparent quad over the whole board, and the blizzard reads the same with half of them. docs/RENDIMIENTO.md.)
    this.fall = new SnowFall(this.ctx.low ? 320 : 1500, new THREE.Vector2(1, 0.12), 0.45 + b * 0.7);
    this.group.add(this.fall.points);
  }

  override paint(hour: Hour): void {
    super.paint(this.place === 'corralDawn' ? 'dawn' : hour);
  }

  override takeLight(f: THREE.PointLight): void {
    super.takeLight(f);
    if (this.place === 'corralFire') {
      // (Reddish, not yellow: the snow and the wool stay cold under it.)
      // (The scene's own fire is there already; the stones and the snow went pink under a strong warm light.)
      f.color.set('#f2d6ca');
      f.position.copy(this.hex(0, 0, 1.0));
      f.intensity = this.lightBase = 3.2;
    }
    if (this.place === 'corralDawn') f.position.copy(this.hex(-3, 4, 1.0));
  }

  /** The corral's sky toward dawn as the night ends (`t` 0 night, 1 dawn). */
  private blendDawn(t: number): void {
    const a = HOURS.night;
    const b = HOURS.dawn;
    const c = new THREE.Color();
    const d = new THREE.Color();
    const mix = (x: string, y: string): THREE.Color => c.set(x).lerp(d.set(y), t);
    const scene = this.ctx.scene;
    if (scene.background instanceof THREE.Color) scene.background.copy(mix(a.bg, b.bg));
    if (scene.fog instanceof THREE.Fog) scene.fog.color.copy(mix(a.fog[0], b.fog[0]));
    const { hemi, key } = this.ctx;
    if (hemi) {
      hemi.color.copy(mix(a.hemi[0], b.hemi[0]));
      hemi.intensity = a.hemi[2] + (b.hemi[2] - a.hemi[2]) * t;
    }
    if (key) {
      key.color.copy(mix(a.key[0], b.key[0]));
      key.intensity = a.key[1] + (b.key[1] - a.key[1]) * t;
    }
    if (this.dome) {
      const s0 = mix(a.sky[0], b.sky[0]).getStyle();
      const s1 = mix(a.sky[1], b.sky[1]).getStyle();
      const s2 = mix(a.sky[2], b.sky[2]).getStyle();
      this.dome.set(s0, s1, s2);
    }
  }

  /* ---------- each tick, draw and frame ---------- */

  override tick(s: BattleState): void {
    super.tick(s);
    const N = this.nv;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.3);
      if (e.kind === 'kindle') {
        this.ctx.flare(p.clone().setY(p.y + 0.4), '#ff8a5a', 2.0, 10);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: 16, color: '#ff8a5a', speed: 1.6, up: 2.0, size: 0.07, life: 14 });
      } else if (e.kind === 'snuffed') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: 10, color: '#59606b', speed: 0.6, up: 1.2, size: 0.22, life: 22, smoke: true });
      } else if (e.kind === 'take') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.25), 0.2, 0.9, '#e8f0ff', 6, 0.6);
      } else if (e.kind === 'thaw') {
        // The frost weeps off the disc: drops and steam.
        const m = this.mirrors[e.n ?? 0];
        if (m) {
          this.ctx.vfx.burst(m.at, { n: 18, color: '#cfe4ff', speed: 1.3, up: 0.4, size: 0.07, life: 14, gravity: 1 });
          this.ctx.vfx.burst(m.at, { n: 6, color: '#dde6f0', speed: 0.4, up: 1.0, size: 0.24, life: 20, smoke: true });
          this.ctx.flare(m.at, '#e8f4ff', 1.6, 8);
        }
        N.beamKey = '';
      } else if (e.kind === 'frost') {
        const m = this.mirrors[e.n ?? 0];
        if (m) this.ctx.vfx.ring(m.at.clone().setY(m.at.y - 1.1), 0.2, 1.2, '#9fd0ff', 8, 0.8);
        N.beamKey = '';
      } else if (e.kind === 'iced') {
        const m = this.mirrors[e.n ?? 0];
        if (m) {
          this.ctx.flare(m.at, '#9fd0ff', 1.4, 8);
          this.ctx.vfx.burst(m.at, { n: 10, color: '#e4eefa', speed: 1.0, up: 0.3, size: 0.06, life: 10 });
        }
      } else if (e.kind === 'mirror') {
        N.beamKey = '';
      } else if (e.kind === 'dive') {
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(p, { n: 22, color: '#dfe6f0', speed: 2.2, up: 1.0, size: 0.12, life: 14 });
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.25), 0.3, 2.6, '#1a1620', 10, 0.8);
      } else if (e.kind === 'frozen') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.25), 0.2, 1.4, '#9fd0ff', 10, 0.9);
        this.ctx.vfx.burst(p, { n: 14, color: '#e4eefa', speed: 1.2, up: 0.8, size: 0.07, life: 12 });
      } else if (e.kind === 'chill') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.6), { n: 6, color: '#b9ccff', speed: 0.6, up: 0.5, size: 0.06, life: 10 });
      } else if (e.kind === 'door') {
        if (this.lampAt) this.ctx.flare(this.lampAt, '#ffffff', 3.0, 14);
      }
    }
  }

  override draw(s: BattleState): void {
    super.draw(s);
    const N = this.nv;
    const w = s.senda?.litter;
    const r = s.rules?.senda?.litter;
    const n = w?.nev;
    if (!w || !r || !n) return;
    // The pool cools as Tessa's cold drops.
    const full = r.snow?.cold.full ?? 10000;
    const cold = Math.max(0, Math.min(1, 1 - n.cold / full));
    if (this.poolMat) (this.poolMat.uniforms.uColor as THREE.IUniform<THREE.Color>).value.set('#ffd6ae').lerp(new THREE.Color('#6f98ff'), cold);
    N.lit = n.lit.slice();
    // The frost: the braziers on the bases, at the pile and in the hands; the circles; the crusts; the beam's mirrors.
    if (N.frost && N.inst) {
      const m4 = new THREE.Matrix4();
      const zero = new THREE.Matrix4().makeScale(0, 0, 0);
      for (let i = 0; i < 6; i++) {
        const p = N.braziers[i];
        N.inst.setMatrixAt(i, n.lit[i] && p ? m4.makeTranslation(p.x, p.y + 0.12, p.z) : zero);
        N.ringLit[i] = n.lit[i] ? 1 : 0;
      }
      for (let i = 0; i < 3; i++) {
        const p = N.pileAt[i] as THREE.Vector3;
        N.inst.setMatrixAt(6 + i, i < n.pile ? m4.makeTranslation(p.x, p.y, p.z) : zero);
      }
      const h = s.heroes[0];
      if (n.carry && h) {
        const at = this.ctx.at(h.x, h.y, 0);
        const yaw = -((h.facing ?? 0) / 1024) * Math.PI * 2 + Math.PI / 2;
        const c = at.clone().add(new THREE.Vector3(Math.sin(yaw) * 0.32, 0.55, Math.cos(yaw) * 0.32));
        N.carry = c;
        N.inst.setMatrixAt(9, m4.compose(c, new THREE.Quaternion(), new THREE.Vector3(0.75, 0.75, 0.75)));
      } else {
        N.carry = null;
        N.inst.setMatrixAt(9, zero);
      }
      N.inst.instanceMatrix.needsUpdate = true;
      N.ice = n.ice.slice();
      const key = w.beam.join(',');
      if (key !== N.beamKey) {
        N.beamKey = key;
        this.aimFrost(s);
      }
    }
    // The corral: the watcher's shadow while a dive is warned; the frozen; dawn coming.
    if (N.corral) {
      const C = r.snow?.corral;
      if (N.shadow && N.shadowMat && C) {
        const on = n.at >= 0;
        N.shadow.visible = on;
        if (on) {
          const k = Math.max(0, Math.min(1, 1 - (n.at - s.waveT) / Math.max(1, C.watcher.warn)));
          const p = this.ctx.at(n.ax, n.ay, 0.08);
          N.shadow.position.copy(p);
          const d = ((C.watcher.r * 2) / 1000) * S * (1.25 - 0.25 * k);
          N.shadow.scale.set(d, d, 1);
          (N.shadowMat.uniforms.uK as THREE.IUniform<number>).value = k;
        }
      }
      N.frozen = [];
      for (const id of n.frozen) {
        const m = s.senda?.npcs.find((x) => x.id === id);
        if (m) N.frozen.push(this.ctx.at(m.x, m.y, 0.4));
      }
      if (C) {
        const t = Math.max(0, Math.min(1, (s.waveT / C.night - 0.72) / 0.28));
        if (Math.abs(t - N.dawn) > 0.01) {
          N.dawn = t;
          this.blendDawn(t);
        }
      }
    }
    // The band behind on the col.
    if (N.col) {
      const j = s.senda?.npcs.find((x) => x.id === 'joran');
      N.band = j ? this.ctx.at(j.x, j.y, 0.05) : null;
    }
    // A thread of smoke off the braziers that are out, now and then.
    if (++N.smokeT % 16 === 0)
      N.braziers.forEach((p, i) => {
        if (N.lit[i] || (N.frost && !N.lit[i])) return;
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.55), { n: 2, color: '#6a717c', speed: 0.2, up: 0.8, size: 0.2, life: 26, smoke: true });
      });
  }

  /** How high the ash watcher flies over its place (world units): high in the storm, coming down while its dive is warned. */
  aloft(s: BattleState, id: number): number {
    const n = s.senda?.litter?.nev;
    const C = s.rules?.senda?.litter?.snow?.corral;
    if (!n || !C || id !== n.boss || s.waveT < n.down) return 0;
    if (n.at >= 0) return 4.5 * Math.max(0, Math.min(1, (n.at - s.waveT) / Math.max(1, C.watcher.warn)));
    return 4.5;
  }

  /** Each frost mirror's disc turned to send the beam where it goes (a frozen one stands by its position alone). */
  private aimFrost(s: BattleState): void {
    const w = s.senda?.litter;
    const L = this.nv.frost;
    if (!w || !L) return;
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i + 1 < w.beam.length; i += 2) pts.push(this.ctx.at(w.beam[i] as number, w.beam[i + 1] as number, 0));
    L.mirrors.forEach((m, i) => {
      const view = this.mirrors[i];
      if (!view) return;
      const mp = this.hex(m.q, m.r);
      for (let k = 1; k + 1 < pts.length; k++) {
        const p = pts[k] as THREE.Vector3;
        if (Math.hypot(p.x - mp.x, p.z - mp.z) > 0.1) continue;
        const a = (pts[k - 1] as THREE.Vector3).clone().sub(p).normalize();
        const b = (pts[k + 1] as THREE.Vector3).clone().sub(p).normalize();
        const nn = a.add(b);
        const dir = nn.lengthSq() < 1e-4 ? (pts[k - 1] as THREE.Vector3).clone().sub(p) : nn;
        view.want = Math.atan2(dir.x, dir.z);
        return;
      }
      view.want = ((w.turns[i] ?? 0) * Math.PI) / 3 + i * 0.7;
    });
  }

  protected override frameOwn(now: number, dt: number): void {
    const N = this.nv;
    const fire = (p: THREE.Vector3, k: number, size = 1): void => {
      const f = 0.85 + 0.15 * Math.sin(now * 9 + k) + 0.06 * Math.sin(now * 23 + k * 3);
      this.glow.add(p.x, p.y + 0.62 * size, p.z, FIRE_CORE, 0.6 * size, 0.95 * f);
      this.glow.add(p.x, p.y + 0.78 * size, p.z, FIRE, 1.7 * size, 0.85 * f);
      this.glow.add(p.x, p.y + 1.15 * size, p.z, EMBER, 0.9 * size, 0.35 * f);
      this.glow.add(p.x, p.y + 0.06, p.z, FIRE, 4.6 * size, 0.3 * f);
      // Embers rising, each on its own cycle.
      for (let j = 0; j < 3; j++) {
        const t = (now * 0.7 + j / 3 + k * 0.13) % 1;
        this.glow.add(p.x + Math.sin(k + j * 2 + now) * 0.15, p.y + 0.7 + t * 1.3 * size, p.z + Math.cos(k + j) * 0.12, EMBER, 0.12, (1 - t) * 0.9);
      }
    };
    N.braziers.forEach((p, i) => {
      if (N.lit[i]) fire(N.frost ? p.clone().setY(p.y + 0.12) : p, i * 1.7);
      // An out brazier on the col or in the corral: its coals dark, a faint red.
      else if (!N.frost) this.glow.add(p.x, p.y + 0.52, p.z, RED, 0.35, 0.18);
    });
    for (const [i, p] of N.fires.entries()) fire(p, 9 + i, 1.2);
    if (N.carry) fire(N.carry.clone().setY(N.carry.y - 0.25), 5, 0.6);
    // The frost: the crusts melt and grow back; the door's dark lamp; the pile's ring.
    if (N.frost) {
      N.shells.forEach((sh, i) => {
        const want = N.ice[i] ?? 1;
        sh.k += (want - sh.k) * Math.min(1, dt * 2.2);
        const k = sh.k;
        sh.m.visible = k > 0.03;
        sh.m.scale.setScalar(0.35 + 0.65 * k);
        const v = this.mirrors[i];
        if (v && k > 0.5) this.glow.add(v.at.x, v.at.y, v.at.z, ICE, 1.0, 0.25 * k);
      });
      if (this.lampAt && (this.lastState?.senda?.litter?.lit ?? -1) < 0) this.glow.add(this.lampAt.x, this.lampAt.y, this.lampAt.z, RED, 0.5, 0.25 + 0.15 * Math.sin(now * 2.4));
      if (N.postTop) this.glow.add(N.postTop.x, N.postTop.y, N.postTop.z, WHITE, 1.2, 0.9);
      const rm = N.rings?.material as THREE.ShaderMaterial | undefined;
      if (rm) (rm.uniforms.uT as THREE.IUniform<number>).value = now % 100;
    }
    // The corral: the frozen glow ice-blue; the shadow's ring pulses.
    for (const p of N.frozen) {
      this.glow.add(p.x, p.y, p.z, ICE, 1.6, 0.6 + 0.2 * Math.sin(now * 4));
      this.glow.add(p.x, p.y + 0.3, p.z, WHITE, 0.6, 0.5);
    }
    if (N.shadowMat) (N.shadowMat.uniforms.uT as THREE.IUniform<number>).value = now % 100;
    // Joran's band: their own warm light a little way behind.
    if (N.band) this.glow.add(N.band.x, N.band.y + 0.1, N.band.z, FIRE, 2.6, 0.16);
  }
}
